import Box from "@mui/material/Box";
import {
  DataGridPro,
  GridColDef,
  GridCsvExportMenuItem,
  GridInitialState,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import { createContext, useContext, useMemo } from "react";
import GridExcelExportMenuItem from "@/components/grid/CustomToolbar/GridExcelExportMenuItem";
import { WorkbookRow } from "@/utilities/xlsx";

interface ExportSettings {
  /** Rows written above the table in the Excel export, see `GridExcelExportMenuItem`. */
  metadata: WorkbookRow[];
  /** Base name of the exported file, without extension. */
  fileName: string;
}

/**
 * How the export settings reach the toolbar.
 *
 * Not through `slotProps`: extra toolbar props need a `ToolbarPropsOverrides` augmentation of
 * `@mui/x-data-grid`, which pnpm does not hoist and TypeScript therefore cannot resolve. Casting
 * the way `DateRangeSelect` does for its field props was tried and is worse here — the grid slot
 * type shares no property with an all-optional props type, so weak-type detection rejects the
 * component as well and a second cast is needed on `slots`, leaving the toolbar props unchecked
 * on both sides. Building the toolbar as a closure instead would remount it — and close any menu
 * the user has open — every time the metadata changes, which is on every vehicle that loads.
 */
const ExportSettingsContext = createContext<ExportSettings>({ metadata: [], fileName: "export" });

const PAGE_SIZE_OPTIONS = [25, 50, 100, 500];

function EnergyReportToolbar() {
  const { metadata, fileName } = useContext(ExportSettingsContext);

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExportContainer>
        <GridCsvExportMenuItem options={{ fileName, utf8WithBom: true }} />
        <GridExcelExportMenuItem options={{ fileName }} metadata={metadata} />
      </GridToolbarExportContainer>
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarQuickFilter />
    </GridToolbarContainer>
  );
}

interface Props<TRow extends GridValidRowModel> extends ExportSettings {
  rows: TRow[];
  columns: GridColDef<TRow>[];
  loading: boolean;
  initialState?: GridInitialState;
}

/**
 * The grid all three views of the report share.
 *
 * One component rather than three near-identical ones, so a column setting, an export option or a
 * page size is changed in one place and the three views cannot drift apart.
 */
export function EnergyReportGrid<TRow extends GridValidRowModel>({
  rows,
  columns,
  loading,
  fileName,
  metadata,
  initialState,
}: Props<TRow>) {
  const exportSettings = useMemo(() => ({ metadata, fileName }), [metadata, fileName]);

  return (
    <ExportSettingsContext.Provider value={exportSettings}>
      <DataGridPro
        rows={rows}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        pagination
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
          ...initialState,
        }}
        slots={{ toolbar: EnergyReportToolbar }}
        sx={{ border: 0 }}
      />
    </ExportSettingsContext.Provider>
  );
}
